import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';

import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const UserIpModal = ({ isOpen, onClose, onSuccess, ipData = null, isLoading = false }) => {
  const [formData, setFormData] = useState({ ip_address: '', name: '', description: '' });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (ipData) {
      setFormData({
        ip_address: ipData.ip_address || '',
        name: ipData.name || '',
        description: ipData.description || '',
      });
    } else {
      setFormData({ ip_address: '', name: '', description: '' });
    }
    setErrors({});
  }, [ipData, isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData?.ip_address?.trim()) {
      newErrors.ip_address = 'IP address is required';
    } else {
      // Basic IP validation (IPv4 or IPv6)
      const ipRegex =
        /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$|^([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$|^::1$|^127\.0\.0\.1$/;
      if (!ipRegex.test(formData.ip_address.trim())) {
        newErrors.ip_address = 'Please enter a valid IP address';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) {
      if (errors.name) {
        toast.error(errors.name);
      } else if (errors.ip_address) {
        toast.error(errors.ip_address);
      }
      return;
    }

    try {
      await onSuccess({
        name: formData?.name?.trim(),
        ip_address: formData?.ip_address?.trim(),
        description: formData?.description?.trim(),
      });
      setFormData({ ip_address: '', name: '', description: '' });
      setErrors({});
      onClose();
    } catch (error) {
      console.error('Error saving IP:', error);
    }
  };

  const isDirty = () => {
    if (!ipData) return true;
    return (
      formData.ip_address.trim() !== (ipData.ip_address || '').trim() ||
      formData.name.trim() !== (ipData.name || '').trim() ||
      formData.description.trim() !== (ipData.description || '').trim()
    );
  };

  const handleClose = () => {
    setFormData({ ip_address: '', name: '', description: '' });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-md shadow-lg rounded-md bg-white">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Icon name="Globe" size={24} color="var(--color-primary)" />
            <h3 className="text-lg font-medium text-gray-900">
              {ipData ? 'Update IP Address' : 'Add New IP Address'}
            </h3>
          </div>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
            <input
              type="text"
              name="name"
              value={formData?.name}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                errors.name ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="e.g., Office WiFi"
              required
            />
            {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">IP Address *</label>
            <input
              type="text"
              name="ip_address"
              value={formData?.ip_address}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                errors.ip_address ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="e.g., 192.168.1.1 or "
              required
            />
            {errors.ip_address && <p className="text-xs text-red-500 mt-1">{errors.ip_address}</p>}
            <p className="text-xs text-gray-500 mt-1">Enter a valid IPv4 or IPv6 address</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              name="description"
              value={formData?.description}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Provide a  description of this IP"
              rows="3"
            />
          </div>

          {/* Buttons */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Cancel
            </button>
            <Button
              type="submit"
              disabled={
                isLoading ||
                !formData?.ip_address?.trim() ||
                !formData?.name?.trim() ||
                (ipData && !isDirty())
              }
              loading={isLoading}
            >
              <Icon name={ipData ? 'Save' : 'Plus'} size={16} className="mr-2" />
              {ipData ? 'Update IP' : 'Add IP'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserIpModal;
